<template>
  <div>
    <navbar :links="['dashboard', $route.name]" />
    <div class="tools">
      <!-- Content type filter menu -->
      <v-menu
        offset-y
        bottom
        right
        style="margin-left: auto"
      >
        <v-btn
          slot="activator"
          class="mx-0 mb-0 mt-3"
          :class="[
            anyFilteringActive ? 'msr--text' : 'grey--text text--darken-1',
          ]"
          icon
          data-cy="filter-menu"
        >
          <v-icon>filter_list</v-icon>
        </v-btn>
        <v-list class="pt-0 no-scrollbar">
          <v-list-tile class="no-padding">
            <v-btn
              block
              flat
              class="pl-0 grey--text"
              @click="resetSitesFilter()"
            >
              <v-layout align-center>
                <v-icon class="ml-3 mr-2">
                  clear
                </v-icon>
                {{ $t('clearFilters') }}
              </v-layout>
            </v-btn>
          </v-list-tile>
          <v-divider />
          <v-list-tile
            class="list-tile-auto-height mt-1 mb-2"
            :disabled="loading"
          >
            <v-layout column>
              <v-checkbox
                v-model="sitesShowRadioDecoders"
                class="msr--text mt-1"
                color="msr"
                hide-details
                label="Radio"
              />
              <v-checkbox
                v-model="sitesShowTVDecoders"
                class="msr--text mt-1"
                color="msr"
                hide-details
                label="TV"
              />
            </v-layout>
          </v-list-tile>
          <!-- Filtering by Uploadgroups -->
          <group-filter-menu-items
            v-if="groupsToFilterSitesBy.length > 0"
            :disabled="loading"
            :groups="groupsToFilterSitesBy"
            :selected-group-ids="sitesSelectedGroupIds"
            @change="onGroupSelected"
          />
        </v-list>
      </v-menu>
      <!-- Tags Filter Menu -->
      <v-slide-x-transition>
        <tag-filter-menu
          v-if="$userprofile && $userprofile.can_use_tagging"
          v-model="sitesSelectedTagIds"
          :tags="$store.state.common.tags"
          :active="sitesSelectedTagIds.length > 0"
          :disabled="loading"
          class="ml-1 pt-3"
        />
      </v-slide-x-transition>
      <!-- Search -->
      <v-text-field
        v-model="sitesSearchText"
        class="ml-3 searchbar-input"
        style="margin-right: auto"
        hide-details
        append-icon="search"
        :label="searchLabel"
        clearable
        @click:clear="() => (sitesSearchText = '')"
        @keyup.native.esc="sitesSearchText = ''"
      />
      <v-btn
        icon
        flat
        class="mr-0 mt-3 mb-0 grey--text text--darken-2"
        :title="$t('expandCollapseAll')"
        @click="toggleAllSitesExpanded()"
      >
        <v-icon>unfold_more</v-icon>
      </v-btn>
    </div>
    <v-list
      dense
      subheader
      class="mt-3"
    >
      <!-- 'No stores existing yet' or 'no stores matching search'-->
      <v-list-tile
        v-if="
          !loading &&
            !$store.state.common.progress.indeterminate &&
            !sites.length
        "
      >
        <!-- 'No stores loaded' -->
        <v-list-tile-sub-title v-if="!sites.length">
          {{ $t('noSites') }}
        </v-list-tile-sub-title>
      </v-list-tile>
      <!-- Upper Pagination Controls -->
      <pagination-controls
        :show="sitesNumPages > 1"
        :page.sync="sitesPageIndex"
        :num-pages="sitesNumPages"
      />
      <!-- Overlayed loading indicator -->
      <v-layout
        v-if="loading"
        justify-center
      >
        <v-progress-circular
          indeterminate
          style="position: fixed"
          class="mt-5 msr--text"
          :size="50"
        />
      </v-layout>
      <!-- Sites -->
      <site
        v-for="(site, index) in sites"
        :key="index"
        :style="{opacity: loading ? 0.5 : 1.0}"
        :site="site"
        :show-divider="index !== sites.length - 1"
        @toggle-expand="toggleSiteExpanded(site)"
        @toggle-expand-all="toggleAllSitesExpanded()"
      />
      <!-- Lower Pagination Controls -->
      <pagination-controls
        :show="sitesNumPages > 1 && sites.length > 10"
        :page.sync="sitesPageIndex"
        :num-pages="sitesNumPages"
      />
    </v-list>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import orderBy from 'lodash.orderby';

import {removeUrlQueryParamsFromCurrentRoute} from '@/utils';

import GroupFilterMenuItems from '@/components/GroupFilterMenuItems';
import Navbar from '@/components/Navbar';
import PaginationControls from '@/components/PaginationControls';
import Site from '@/components/Site';
import TagFilterMenu from '@/components/TagFilterMenu';

export default {
  components: {
    GroupFilterMenuItems,
    Navbar,
    PaginationControls,
    Site,
    TagFilterMenu,
  },
  data() {
    return {
      loading: false,
      expandAllOnNextFetched: false,
    };
  },
  computed: {
    groupsToFilterSitesBy() {
      return this.$store.getters.accessibleGroups.filter(
        (group) =>
          group.kind !== 'INDUSTRY' && group.visible_in_app_content_filters
      );
    },
    searchLabel() {
      if (this.sites.length > 0) {
        return `${this.$t('search')} (${this.sitesOverallCount}) ...`;
      }
      return this.$t('search') + ' ...';
    },
    sitesPageIndex: {
      get() {
        return this.$store.state.common.sitesPageIndex;
      },
      set(index) {
        this.$store.commit('SET_SITES_PAGE_INDEX', index);
      },
    },
    sitesSearchText: {
      get() {
        return this.$store.state.common.sitesSearchText;
      },
      set(text) {
        this.$store.commit('SET_SITES_SEARCH_TEXT', text);
      },
    },
    sitesShowRadioDecoders: {
      get() {
        return this.$store.state.common.sitesShowRadioDecoders;
      },
      set(flag) {
        this.$store.commit('SET_SITES_SHOW_RADIO_DECODERS', flag);
      },
    },
    sitesShowTVDecoders: {
      get() {
        return this.$store.state.common.sitesShowTVDecoders;
      },
      set(flag) {
        this.$store.commit('SET_SITES_SHOW_TV_DECODERS', flag);
      },
    },
    sitesSelectedGroupIds() {
      return this.$store.state.common.sitesSelectedGroupIds;
    },
    sitesSelectedTagIds: {
      get() {
        return this.$store.state.common.sitesSelectedTagIds;
      },
      set(tagIds) {
        this.$store.commit('SET_SITES_SELECTED_TAG_IDS', tagIds);
      },
    },
    /**
     * 'Active' here means, that the default 'show' all has been changed.
     */
    contentTypeFilteringActive() {
      return !(
        (this.sitesShowRadioDecoders && this.sitesShowTVDecoders) ||
        (!this.sitesShowRadioDecoders && !this.sitesShowTVDecoders)
      );
    },
    groupFilteringActive() {
      return this.sitesSelectedGroupIds.length > 0;
    },
    anyFilteringActive() {
      return this.contentTypeFilteringActive || this.groupFilteringActive;
    },
    sitesOverallCount() {
      return this.$store.state.common.sitesPage.count;
    },
    sitesNumPages() {
      return this.$store.state.common.sitesPage.num_pages;
    },
    sites() {
      return this.$store.state.common.sitesPage.results.sites;
    },
  },
  watch: {
    sitesPageIndex: function () {
      const reset = false;
      this.refetchSites(reset);
    },
    sitesShowRadioDecoders: function () {
      this.refetchSites();
    },
    sitesShowTVDecoders: function () {
      this.refetchSites();
    },
    sitesSearchText: debounce(function () {
      this.refetchSites();
    }, 250),
    sitesSelectedGroupIds: {
      deep: true,
      handler: function () {
        this.refetchSites();
      },
    },
    sitesSelectedTagIds: {
      deep: true,
      handler: function () {
        this.refetchSites();
      },
    },
  },
  mounted() {
    if (this.sites.length === 0) {
      this.fetchSites();
    }

    this.$api.updateUserLastSeen('sites').then(() => {
      this.$store.dispatch('RETRIEVE_NUM_UNSEEN');
    });
  },
  methods: {
    handleInitialQueryParams() {
      if (this.$route.query && this.$route.query.search) {
        this.sitesSearchText = this.$route.query.search;
        this.expandAllOnNextFetched = true;
        removeUrlQueryParamsFromCurrentRoute();
      }
    },
    toggleSiteExpanded(site, expanded = undefined) {
      this.$store.commit('SET_SITE_EXPANDED', {
        site: site,
        expanded: expanded || !site.expanded,
      });
    },
    toggleAllSitesExpanded() {
      const allExpanded =
        this.sites.filter((site) => site.expanded).length == this.sites.length;
      const expanded = allExpanded ? false : true;
      this.setAllSitesExpanded(expanded);
    },
    setAllSitesExpanded(expanded) {
      for (const site of this.sites) {
        this.toggleSiteExpanded(site, expanded);
      }
    },
    onGroupSelected(group, selected) {
      let mutation;
      if (selected) {
        mutation = 'ADD_SITES_SELECTED_GROUP_ID';
      } else {
        mutation = 'REMOVE_SITES_SELECTED_GROUP_ID';
      }
      this.$store.commit(mutation, group.id);
    },
    getSortedDecodersForSite(site) {
      let decoders = [].concat(site.radiodecoders).concat(site.tvdecoders);
      return orderBy(decoders, 'description');
    },
    _siteHasDecodersOfType(site, ctype) {
      let decoders = this.getSortedDecodersForSite(site);
      for (var i = 0; i < decoders.length; i++) {
        let decoder = decoders[i];
        if (decoder.ctype === ctype) {
          return true;
        }
      }
      return false;
    },
    shouldDisplaySite(site) {
      if (
        (this.sitesShowRadioDecoders && this.sitesShowTVDecoders) ||
        (!this.sitesShowRadioDecoders && !this.sitesShowTVDecoders)
      ) {
        return true;
      }

      if (this.sitesShowRadioDecoders) {
        let hasRadio = this._siteHasDecodersOfType(site, 'radio');
        return hasRadio;
      }
      if (this.sitesShowTVDecoders) {
        let hasTV = this._siteHasDecodersOfType(site, 'tv');
        return hasTV;
      }
      return false;
    },
    toggleSortDirection() {
      if (this.sortDirection === 'desc') {
        this.sortDirection = 'asc';
      } else {
        this.sortDirection = 'desc';
      }
    },
    resetSitesFilter() {
      this.$store.commit('RESET_SITES_FILTERS');
      this.$store.commit('RESET_SITES');
    },
    fetchSites() {
      this.loading = true;
      return this.$store
        .dispatch('LIST_SITES', this.sitesPageIndex)
        .then(() => {
          this.loading = false;

          if (this.expandAllOnNextFetched) {
            this.setAllSitesExpanded(true);
            this.expandAllOnNextFetched = false;
          }
        });
    },
    refetchSites(reset = true) {
      if (reset) {
        this.$store.commit('RESET_SITES');
      }
      this.fetchSites();
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.handleInitialQueryParams();
    });
  },
};
</script>

<style scoped>
.tools {
  display: flex;
  align-items: center;
}

.searchbar-input {
  max-width: 400px;
}

h3 {
  color: grey;
}

.current-search {
  display: flex;
  justify-content: center;
}

.v-menu__content >>> .no-scrollbar {
  overflow-y: hidden;
}

.no-padding >>> .v-list__tile {
  padding: 0;
}
</style>



// WEBPACK FOOTER //
// src/pages/Sites.vue